import { useQuery } from '@tanstack/react-query'
import { useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { EyeIcon, ChevronUpIcon, ChevronDownIcon, MagnifyingGlassIcon } from '@heroicons/react/20/solid'
import { ArchiveBoxIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline'
import { productsApi, type ProductRecord } from '../../lib/api'
import { Pagination } from '../../components/ui/Pagination'

type SortField = 'itemCode' | 'itemName' | 'itemsGroupName' | 'defaultPrice' | 'valid'
type SortDirection = 'asc' | 'desc'

const VALID_STATUSES = [
  { value: 'all', label: 'All' },
  { value: 'tYES', label: 'Active' },
  { value: 'tNO', label: 'Inactive' },
] as const

const ITEM_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'salesItem', label: 'Sales Items' },
  { value: 'purchaseItem', label: 'Purchase Items' },
] as const

export function ProductsListPage() {
  const [validFilter, setValidFilter] = useState<string>('all')
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [sortField, setSortField] = useState<SortField>('itemCode')
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)

  // Build filters object for API
  const filters: Record<string, string> = {}
  if (validFilter !== 'all') filters.valid = validFilter
  if (typeFilter === 'salesItem') filters.salesItem = 'true'
  if (typeFilter === 'purchaseItem') filters.purchaseItem = 'true'
  if (searchQuery) filters.search = searchQuery

  const { data: response, isLoading } = useQuery({
    queryKey: ['products', { page, limit, filters }],
    queryFn: () => productsApi.list({ page, limit, filters: Object.keys(filters).length > 0 ? filters : undefined }),
  })

  const { data: stats } = useQuery({
    queryKey: ['products', 'stats'],
    queryFn: () => productsApi.getStats(),
  })

  const products = response?.data

  // Handle filter changes - reset to page 1
  const handleValidFilter = (value: string) => {
    setValidFilter(value)
    setPage(1)
  }

  const handleTypeFilter = (value: string) => {
    setTypeFilter(value)
    setPage(1)
  }

  const handleSearch = (query: string) => {
    setSearchQuery(query)
    setPage(1)
  }

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Client-side sorting
  const sortedProducts = useMemo(() => {
    if (!products) return []

    const result = [...products]

    result.sort((a, b) => {
      let comparison = 0

      switch (sortField) {
        case 'itemCode':
          comparison = a.itemCode.localeCompare(b.itemCode)
          break
        case 'itemName':
          comparison = a.itemName.localeCompare(b.itemName)
          break
        case 'itemsGroupName':
          comparison = (a.itemsGroupName || '').localeCompare(b.itemsGroupName || '')
          break
        case 'defaultPrice':
          comparison = parseFloat(a.defaultPrice || '0') - parseFloat(b.defaultPrice || '0')
          break
        case 'valid':
          comparison = a.valid.localeCompare(b.valid)
          break
      }

      return sortDirection === 'asc' ? comparison : -comparison
    })

    return result
  }, [products, sortField, sortDirection])

  const SortableHeader = ({ field, children }: { field: SortField; children: React.ReactNode }) => {
    const isActive = sortField === field
    return (
      <th
        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900 cursor-pointer hover:bg-gray-100 select-none"
        onClick={() => handleSort(field)}
      >
        <div className="flex items-center gap-1">
          {children}
          <div className="flex flex-col -space-y-1">
            <ChevronUpIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'asc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
            <ChevronDownIcon
              className={`h-3 w-3 ${isActive && sortDirection === 'desc' ? 'text-gray-900' : 'text-gray-300'}`}
            />
          </div>
        </div>
      </th>
    )
  }

  const formatPrice = (price: string | null, currency: string) => {
    if (!price) return '-'
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency }).format(parseFloat(price))
  }

  if (isLoading) return <div className="p-8">Loading...</div>

  return (
    <>
      {/* Secondary column - Actions and filters */}
      <aside className="fixed inset-y-4 left-72 hidden w-[calc((100vw-18rem-2rem)/3)] overflow-y-auto bg-white px-4 py-6 sm:px-6 lg:px-8 xl:block rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)]">
        <div className="space-y-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Products</h2>
            <p className="mt-1 text-sm text-gray-500">
              Browse and manage product catalog
            </p>
          </div>

          {/* Search */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Search</h3>
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by code or name..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Status</h3>
            <div className="flex flex-wrap gap-2">
              {VALID_STATUSES.map(status => (
                <button
                  key={status.value}
                  onClick={() => handleValidFilter(status.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    validFilter === status.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status.label}
                </button>
              ))}
            </div>
          </div>

          {/* Type Filter */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Item Type</h3>
            <div className="flex flex-wrap gap-2">
              {ITEM_TYPES.map(type => (
                <button
                  key={type.value}
                  onClick={() => handleTypeFilter(type.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-colors ${
                    typeFilter === type.value
                      ? 'bg-gray-900 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {type.label}
                </button>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="bg-white p-4 rounded-lg shadow-sm ring-1 ring-gray-900/5">
            <h3 className="text-sm font-medium text-gray-900 mb-3">Quick Stats</h3>
            <dl className="space-y-2">
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Active</dt>
                <dd className="font-medium text-gray-900">{stats?.active || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Sales Items</dt>
                <dd className="font-medium text-gray-900">{stats?.salesItems || 0}</dd>
              </div>
              <div className="flex justify-between text-sm">
                <dt className="text-gray-500">Purchase Items</dt>
                <dd className="font-medium text-gray-900">{stats?.purchaseItems || 0}</dd>
              </div>
              <div className="flex justify-between text-sm border-t pt-2">
                <dt className="text-gray-500">Total</dt>
                <dd className="font-medium text-gray-900">{stats?.total || 0}</dd>
              </div>
            </dl>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="xl:pl-[calc((100vw-18rem-2rem)/3)] xl:ml-4 p-4 xl:p-0 xl:pr-4 xl:py-4">
        <div className="bg-white rounded-xl shadow-[4px_4px_12px_rgba(0,0,0,0.08)] min-h-[calc(100vh-2rem)] px-4 py-10 sm:px-6 lg:px-8 lg:py-6 overflow-hidden">
          {/* Mobile header */}
          <div className="xl:hidden mb-6">
            <h1 className="text-2xl font-semibold text-gray-900">Products</h1>
            <div className="mt-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by code or name..."
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  className="pl-9 block w-full rounded-md border-gray-300 shadow-sm focus:border-brand-500 focus:ring-brand-500 sm:text-sm"
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <SortableHeader field="itemCode">Item Code</SortableHeader>
                  <SortableHeader field="itemName">Name</SortableHeader>
                  <SortableHeader field="itemsGroupName">Group</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Unit</th>
                  <SortableHeader field="defaultPrice">Price</SortableHeader>
                  <SortableHeader field="valid">Status</SortableHeader>
                  <th className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {sortedProducts.map((product) => (
                  <tr key={product.id} className="hover:bg-gray-50">
                    <td className="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
                      <Link to={`/products/${product.id}`} className="hover:text-brand-600 flex items-center gap-2">
                        <ArchiveBoxIcon className="h-4 w-4 text-gray-400" />
                        {product.itemCode}
                      </Link>
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500 max-w-xs truncate">
                      {product.itemName}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {product.itemsGroupName || '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {product.salesUnit}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                      {formatPrice(product.defaultPrice, product.defaultCurrency)}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      {product.valid === 'tYES' ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                          <CheckCircleIcon className="h-3 w-3" />
                          Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">
                          <XCircleIcon className="h-3 w-3" />
                          Inactive
                        </span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-3 py-4 text-sm">
                      <Link
                        to={`/products/${product.id}`}
                        className="text-brand-600 hover:text-brand-900"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                    </td>
                  </tr>
                ))}
                {sortedProducts.length === 0 && (
                  <tr>
                    <td colSpan={7} className="px-3 py-8 text-center text-sm text-gray-500">
                      No products found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {response && response.totalPages > 1 && (
            <div className="mt-6">
              <Pagination
                page={page}
                totalPages={response.totalPages}
                total={response.total}
                limit={limit}
                onPageChange={setPage}
                onLimitChange={(newLimit) => {
                  setLimit(newLimit)
                  setPage(1)
                }}
              />
            </div>
          )}
        </div>
      </div>
    </>
  )
}
